import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { BancoPagamento } from "../../domain/models/banco_pagamento";
import { initialBancoPagamentoFiltersValues, useBancoPagamentoListaFilters } from "../hooks/banco_pagamento_lista/filters";
import { useBancoPagamentoListaPagination } from "../hooks/banco_pagamento_lista/pagination";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { BancoPagamentoForm, BancoPagamentoSchema, defaultBancoPagamentoForm } from "../schemas/BancoPagamento_lista/BancoPagamento_form";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

type LoadBancoPagamentoListaState = {

    idsBancoPagamento?: number[];
    status?: number | null;
    page?: number;
    rowsPerPage?: number;

}

export const useBancoPagamentoListaViewModel = () => {

    const {
        financeiro: {
            getAllBancoPagamentoWithFiltersUseCase,
            getBancoPagamentoByIdUseCase,
            createBancoPagamentoUseCase,
            updateBancoPagamentoUseCase,
            deleteBancoPagamentoByIdUseCase

        },
        commons: {
            getBancosPagamentoDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase
        },

    } = useDependencyInjection();

    const hookForm = useForm<BancoPagamentoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(BancoPagamentoSchema),

    });

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [selectedIdBancoPagamento, setSelectedIdBancoPagamento] = useState<number>();
    const [openBancoPagamentoCadastroModal, setOpenBancoPagamentoCadastroModal] = useState(false);
    const [bancoPagamento, setBancoPagamento] = useState<BancoPagamento>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [bancoPagamentoLista, setBancoPagamentoLista] = useState<BancoPagamento[]>();
    const [ddlBancoPagamento, setDdlBancoPagamento] = useState<DropDown[]>();


    const filters = useBancoPagamentoListaFilters();
    const pagination = useBancoPagamentoListaPagination();


    //#region CARREGAMENTO.


    const loadBancoPagamentoLista = useCallback(async (funcState?: LoadBancoPagamentoListaState) => {

        try {

            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredIdBancoPagamento = funcState?.idsBancoPagamento ?? filters.idsBancoPagamento;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllBancoPagamentoWithFiltersUseCase.getAllBancoPagamentoWithFilters(
                filteredIdBancoPagamento,
                filteredStatus ?? 2,
                filteredPage,
                filteredRowsPerPage
            );

            setBancoPagamentoLista(getAllBancoPagamentoWithFiltersUseCase.items);

            pagination.setRowsPerPage(getAllBancoPagamentoWithFiltersUseCase.pageSize);
            pagination.setTotalItems(getAllBancoPagamentoWithFiltersUseCase.totalItems);
        } catch (e: unknown) {
            setBancoPagamentoLista([]);
            throw new Error("Falha ao buscar bancos!");
        }
    }, [
        filters,
        pagination,
        getAllBancoPagamentoWithFiltersUseCase,
    ]);

    const loadBancoPagamentoById = useCallback(async (idBancoPagamento: number) => {
        try {
            setPageLoading(true);
            const result = await getBancoPagamentoByIdUseCase.getBancoPagamentoById(idBancoPagamento)
            setBancoPagamento(result);
            setSelectedIdBancoPagamento(result.idBancoPagamento)

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar banco!'));
        } finally {
            setPageLoading(false);
        }

    }, [getBancoPagamentoByIdUseCase, pushErrorNotification]);


    const loadBancoPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBancoPagamento(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar banco!'));
        } finally {
            setPageLoading(false);
        }

    }, [getBancosPagamentoDropDownUseCase]);


    //#endregion

    usePageStartBootstrap(async () => {
        try {
            if (bancoPagamentoLista === undefined) {
                await loadBancoPagamentoLista();
                await loadBancoPagamentoDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de Bancos!")[0]);
        }
    })


    const handleBancoPagamentoEditOpenState = useCallback(async (idBancoPagamento: number | null, openState: boolean) => {
        try {
            if (openState) {
                if (idBancoPagamento) {
                    const result = await getBancoPagamentoByIdUseCase.getBancoPagamentoById(idBancoPagamento);
                    setBancoPagamento({
                        ...result,
                        idBancoPagamento: idBancoPagamento
                    });
                    setSelectedIdBancoPagamento(idBancoPagamento);
                    setOpenBancoPagamentoCadastroModal(openState);
                } else {
                    setBancoPagamento(undefined);
                    setSelectedIdBancoPagamento(undefined);
                    setOpenBancoPagamentoCadastroModal(openState);

                }
            } else {
                setBancoPagamento(undefined);
                setSelectedIdBancoPagamento(undefined);
                setOpenBancoPagamentoCadastroModal(openState);
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar banco!")[0]);
        }

    }, [
        getBancoPagamentoByIdUseCase,
        setErrorMessage
    ]);

    const handleNewBancoPagamento = useCallback(() => {
        try {
            setBancoPagamento(undefined);
            setSelectedIdBancoPagamento(undefined);
            setOpenBancoPagamentoCadastroModal(true);
            hookForm.reset(defaultBancoPagamentoForm);

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar banco!")[0]);
        }
    }, [
        hookForm,
        setErrorMessage
    ]);


    //#region CADASTRO | UPDATE.
    const handleCadastroBancoPagamentoSubmit = useCallback(async (form: BancoPagamentoForm) => {
        try {

            setSaving(true);
            if (selectedIdBancoPagamento) {
                await updateBancoPagamentoUseCase.updateBancoPagamento({
                    idBancoPagamento: selectedIdBancoPagamento,
                    ativo: form.ativo,
                    controlaSaldo: form.controlaSaldo,
                    nomeBanco: form.nomeBanco,
                    nrAgencia: form.nrAgencia,
                    nrConta: form.nrConta,
                })
                await loadBancoPagamentoById(selectedIdBancoPagamento);
                await loadBancoPagamentoDropDown();
                await loadBancoPagamentoLista();
                setOpenBancoPagamentoCadastroModal(false);
                setSelectedIdBancoPagamento(undefined);
                setBancoPagamento(undefined);
                pushInfoNotification("Sucesso", "Banco editado com sucesso!", { isAutoFinish: true });

            } else {
                const idNewBancoPagamento = await createBancoPagamentoUseCase.createBancoPagamento({
                    idBancoPagamento: 0,
                    ativo: form.ativo,
                    controlaSaldo: form.controlaSaldo,
                    nomeBanco: form.nomeBanco,
                    nrAgencia: form.nrAgencia,
                    nrConta: form.nrConta,
                })
                await loadBancoPagamentoById(idNewBancoPagamento);
                await loadBancoPagamentoDropDown();
                await loadBancoPagamentoLista();
                setOpenBancoPagamentoCadastroModal(false);
                setSelectedIdBancoPagamento(undefined);
                setBancoPagamento(undefined);
                pushInfoNotification("Sucesso", "Banco salvo com sucesso!", { isAutoFinish: true });

            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Receber Contas!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdBancoPagamento,
        updateBancoPagamentoUseCase,
        createBancoPagamentoUseCase,
        loadBancoPagamentoById,
        loadBancoPagamentoDropDown,
        loadBancoPagamentoLista,
        pushInfoNotification,
        pushErrorNotification,
    ]);



    //#endregion





    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadBancoPagamentoLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [loadBancoPagamentoLista]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadBancoPagamentoLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadBancoPagamentoLista]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadBancoPagamentoLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadBancoPagamentoLista]);

    //#endregion






    //#region DELECAO

    const handleBancoPagamentoDeleteOpenState = useCallback((idBancoPagamento: number | null, openState: boolean) => {
        if (openState) {
            if (idBancoPagamento) {
                setSelectedIdBancoPagamento(idBancoPagamento);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdBancoPagamento(undefined);
                setSelectedIdBancoPagamento(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, [bancoPagamentoLista]);


    const handleConfirmarDeletarBancoPagamento = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdBancoPagamento) {
                await deleteBancoPagamentoByIdUseCase.deleteBancoPagamentoById(selectedIdBancoPagamento)
                pushInfoNotification("Sucesso", "Banco Deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Banco!", { isAutoFinish: true });

            }

            await loadBancoPagamentoDropDown()
            await loadBancoPagamentoLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Contas!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdBancoPagamento(undefined)
        }
    }, [
        selectedIdBancoPagamento,
        deleteBancoPagamentoByIdUseCase,
        loadBancoPagamentoLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region PESQUISA.

    const handleIdsBancoPagamentoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsBancoPagamento(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadBancoPagamentoLista({ idsBancoPagamento: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar Bancos!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadBancoPagamentoLista,]
    );


    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
        filters.setStatus(status)
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadBancoPagamentoLista({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadBancoPagamentoLista]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadBancoPagamentoLista({
                ...initialBancoPagamentoFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadBancoPagamentoLista, pushErrorNotification]);

    //#endregion 



    //#region History

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(bancoPagamento?.idBancoPagamento ?? 0, LogTipoTabela.BANCO_PAGAMENTO, true);
            }
        }
    }, [
        bancoPagamento,
        changeHistory,
    ]);

    //#endregion



    return {
        loading,
        saving,
        bancoPagamentoLista: bancoPagamentoLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlBancoPagamento: ddlBancoPagamento ?? [],
        selectedIdBancoPagamento,
        openBancoPagamentoCadastroModal,
        bancoPagamento,
        hookForm,
        changeHistory,
        handleOpenChangeHistory,
        setOpenBancoPagamentoCadastroModal,
        handleIdsBancoPagamentoChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleBancoPagamentoEditOpenState,
        handleBancoPagamentoDeleteOpenState,
        handleNewBancoPagamento,
        handleConfirmarDeletarBancoPagamento,
        handleCadastroBancoPagamentoSubmit

    };
}
